import { useState, useEffect } from 'react'
import { EVENTS } from '../data/content'

function CalendarSection() {
    const today = new Date()
    const [currentDate, setCurrentDate] = useState(new Date())
    const [currentEventIndex, setCurrentEventIndex] = useState(0)

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ]
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay()
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

    // Get events for the current month view
    const getEventsForDate = (day) => {
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        return EVENTS.filter(e => e.date === dateStr)
    }

    const prevMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth - 1, 1))
    }

    const nextMonth = () => {
        setCurrentDate(new Date(currentYear, currentMonth + 1, 1))
    }

    const goToToday = () => {
        setCurrentDate(new Date())
    }

    // Prepare calendar days
    const calendarDays = []
    // Add empty slots for days before the 1st
    for (let i = 0; i < firstDayOfMonth; i++) {
        calendarDays.push(null)
    }
    // Add actual days
    for (let i = 1; i <= daysInMonth; i++) {
        calendarDays.push(i)
    }

    // Upcoming events (all)
    const upcomingEvents = EVENTS.filter(ev => new Date(ev.date + 'T00:00:00') >= new Date(new Date().setHours(0, 0, 0, 0)))
        .sort((a, b) => new Date(a.date + 'T00:00:00') - new Date(b.date + 'T00:00:00'))

    // Auto-rotate carousel every 3.5 seconds
    useEffect(() => {
        if (upcomingEvents.length <= 1) return

        const interval = setInterval(() => {
            setCurrentEventIndex((prev) => (prev + 1) % upcomingEvents.length)
        }, 3500)

        return () => clearInterval(interval)
    }, [upcomingEvents.length])

    const nextEvent = () => {
        setCurrentEventIndex((prev) => (prev + 1) % upcomingEvents.length)
    }

    const prevEvent = () => {
        setCurrentEventIndex((prev) => (prev - 1 + upcomingEvents.length) % upcomingEvents.length)
    }

    return (
        <section className="py-24 px-6 bg-[#0a192f] relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none"></div>

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">UPCOMING EVENTS</h2>
                    <p className="text-xl text-blue-100/60 max-w-2xl mx-auto">
                        Stay up to date with our workshops, meetings, and community events.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-12">
                    {/* Calendar Grid */}
                    <div className="lg:col-span-7">
                        <div className="bg-white/5 backdrop-blur-xl rounded-[32px] border border-white/10 p-8 shadow-2xl">
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-2xl font-bold text-white">
                                    {monthNames[currentMonth]} <span className="text-blue-400">{currentYear}</span>
                                </h3>
                                <div className="flex gap-3">
                                    <button
                                        onClick={goToToday}
                                        className="px-4 py-2 text-sm font-bold text-blue-200 border border-white/10 rounded-xl hover:bg-white/5 transition-all"
                                    >
                                        Today
                                    </button>
                                    <div className="flex bg-white/5 rounded-xl border border-white/10">
                                        <button
                                            onClick={prevMonth}
                                            className="p-2.5 text-blue-200 hover:text-white transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <div className="w-[1px] bg-white/10 my-2"></div>
                                        <button
                                            onClick={nextMonth}
                                            className="p-2.5 text-blue-200 hover:text-white transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-7 gap-2 mb-2">
                                {dayNames.map(day => (
                                    <div key={day} className="text-center py-2 text-xs font-bold text-blue-400/60 uppercase tracking-widest">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 gap-2">
                                {calendarDays.map((day, idx) => {
                                    if (day === null) return <div key={`empty-${idx}`} className="aspect-square"></div>

                                    const dayEvents = getEventsForDate(day)
                                    const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear()

                                    return (
                                        <div
                                            key={day}
                                            className={`aspect-square relative rounded-2xl border transition-all flex flex-col items-center justify-center cursor-default group
                                                ${isToday ? 'bg-blue-600 border-blue-400 shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'bg-white/5 border-white/5 hover:border-white/20 hover:bg-white/10'}
                                                ${dayEvents.length > 0 && !isToday ? 'border-blue-400/30' : ''}
                                            `}
                                        >
                                            <span className={`text-lg font-bold ${isToday ? 'text-white' : 'text-blue-50/90'}`}>
                                                {day}
                                            </span>
                                            {dayEvents.length > 0 && (
                                                <div className={`absolute bottom-2 w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-blue-400'}`}></div>
                                            )}

                                            {/* Tooltip on hover if events exist */}
                                            {dayEvents.length > 0 && (
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900/95 backdrop-blur-xl border border-blue-400/30 p-3 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20 shadow-2xl">
                                                    {dayEvents.map((ev, i) => (
                                                        <div key={i} className="text-xs text-white font-medium mb-1 last:mb-0">
                                                            • {ev.title}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Events Carousel */}
                    <div className="lg:col-span-5 flex flex-col">
                        <div className="flex-grow">
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                                    <span className="w-2 h-8 bg-blue-500 rounded-full"></span>
                                    EVENT DETAILS
                                </h3>
                                {upcomingEvents.length > 1 && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={prevEvent}
                                            className="p-2 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
                                        >
                                            <svg className="w-5 h-5 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                        </button>
                                        <button
                                            onClick={nextEvent}
                                            className="p-2 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-all"
                                        >
                                            <svg className="w-5 h-5 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {upcomingEvents.length > 0 ? (
                                <div className="relative overflow-hidden">
                                    <div
                                        className="flex transition-transform duration-500 ease-in-out"
                                        style={{ transform: `translateX(-${currentEventIndex * 100}%)` }}
                                    >
                                        {upcomingEvents.map((ev, idx) => (
                                            <div
                                                key={idx}
                                                className="min-w-full"
                                            >
                                                <div className="bg-white/5 backdrop-blur-md rounded-[24px] border border-white/10 p-8 hover:bg-white/10 transition-all duration-300">
                                                    <div className="flex items-start justify-between mb-6">
                                                        <div className="px-5 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-300 text-base font-bold uppercase tracking-wider">
                                                            {new Date(ev.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                        </div>
                                                        <div className="text-blue-100/60 text-base font-medium">
                                                            {ev.time}
                                                        </div>
                                                    </div>
                                                    <h4 className="text-2xl font-bold text-white mb-4">
                                                        {ev.title}
                                                    </h4>
                                                    <p className="text-blue-100/60 text-base leading-relaxed mb-6">
                                                        {ev.description}
                                                    </p>
                                                    <div className="flex items-center gap-2 text-blue-300 text-base font-semibold">
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                                        {ev.location}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Carousel Indicators */}
                                    {upcomingEvents.length > 1 && (
                                        <div className="flex justify-center gap-2 mt-6">
                                            {upcomingEvents.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => setCurrentEventIndex(idx)}
                                                    className={`h-2 rounded-full transition-all ${idx === currentEventIndex
                                                        ? 'w-8 bg-blue-500'
                                                        : 'w-2 bg-white/20 hover:bg-white/40'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-20 bg-white/5 rounded-[32px] border border-dashed border-white/10">
                                    <p className="text-blue-100/40 text-lg">No upcoming events scheduled.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CalendarSection
